<template>
  <v-container class="chat-container" light>
    <v-row class="chat-root ma-0">
      <div class="chat-content">
        <template v-if="!messages || messages.length < 1">
          <div class="chat-empty-state">
            <h2 class="chat-empty-state-title">
              {{ $t('aiChat.chatDescription') }}
            </h2>
          </div>
        </template>
        <template v-else>
          <div
            class="chat-message-stream"
            :style="{ marginBottom: isLoading ? '40px' : '0px' }"
          >
            <template v-for="(answer, index) in messages">
              <template v-if="answer.role === USER">
                <div class="chat-message-user" :key="index">
                  <div class="chat-message-user-message">
                    {{ answer.content }}
                  </div>
                </div>
              </template>
              <template v-else-if="answer.role === ASSISTANT">
                <div class="chat-message-gpt" :key="`assistant-${index}`">
                  <Answer
                    :vSessionId="vSessionId"
                    :answer="{
                      answer: answer.content,
                      citations: parseCitationFromMessage(messages[index - 1]),
                      message_id: answer.id,
                      feedback: answer.feedback,
                    }"
                  />
                </div>
              </template>
              <template v-else-if="answer.role === ERROR">
                <div class="chat-message-error" :key="`error-${index}`">
                  <v-row class="chat-message-error-content mb-1 pl-2">
                    <v-icon
                      small
                      class="error-icon"
                      color="rgba(182, 52, 67, 1)"
                      >mdi-alert-circle</v-icon
                    >
                    <div>{{ $t('validationText.error') }}</div>
                  </v-row>
                  <div class="chat-message-error-content">
                    {{ answer.content }}
                  </div>
                </div>
              </template>
            </template>
            <template v-if="showLoadingMessage">
              <div class="chat-message-gpt">
                <Answer
                  :answer="{
                    answer: generatingAnswer,
                    citations: [],
                  }"
                  @citationClicked="() => null"
                />
              </div>
            </template>
            <div ref="chatMessageStreamEnd" />
          </div>
        </template>

        <v-row class="chat-input">
          <template v-if="isLoading">
            <v-row
              class="d-flex justify-center align-center stop-generating-container"
              aria-label="Stop generating"
            >
              <v-btn small rounded light @click="stopGenerating">
                <v-icon
                  small
                  class="stop-generating-icon mr-1"
                  aria-hidden="true"
                >
                  mdi-stop
                </v-icon>
                {{ $t('aiChat.stopGenerating') }}
              </v-btn>
            </v-row>
          </template>
          <QuestionInput
            clearOnSend
            :placeholder="questionInputLabel"
            :disabled="isLoading"
            @send="makeApiRequestWithCosmosDB"
            :conversationId="currentChat?.id ? currentChat?.id : undefined"
          />
        </v-row>
      </div>
      <template v-if="isChatHistoryOpen">
        <ChatHistoryPanel
          @hideHistory="hideHistory"
          :showSpinner="showSpinner"
          :groupedChatHistory="groupedChatHistory"
          :vSessionId="vSessionId"
          @selectHistory="selectHistory"
          @loadMoreHistory="loadMoreHistory"
        />
      </template>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapGetters, mapState, mapActions } from 'vuex'
import QuestionInput from './QuestionInput.vue'
import Answer from './Answer.vue'
import { v4 as uuidv4 } from 'uuid'
import {
  ChatMessage,
  ASSISTANT,
  TOOL,
  ERROR,
  NO_CONTENT_ERROR,
  ChatResponse,
  ToolMessageContent,
  messageStatus,
  Conversation,
  USER,
} from '@/constants/aiChatModels'
import { isEmpty } from 'lodash'
import ChatHistoryPanel from './ChatHistoryPanel.vue'

export default defineComponent({
  name: 'Chat',
  props: {
    isChatHistoryOpen: {
      type: Boolean,
      default: false,
    },
    vSessionId: {
      type: String,
      default: '',
    },
    groupedChatHistory: {
      type: Array,
      default: () => [],
    },
    showSpinner: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    QuestionInput,
    Answer,
    ChatHistoryPanel,
  },
  data() {
    return {
      ERROR,
      USER,
      ASSISTANT,
      isLoading: false,
      showLoadingMessage: false,
      messages: [],
      abortFunc: AbortController,
      assistantMessage: {} as ChatMessage,
      toolMessage: {} as ChatMessage,
      assistantContent: '',
      processMessages: '',
    }
  },
  computed: {
    ...mapState('Chat', ['chatHistory', 'currentChat']),
    ...mapGetters('Security', ['userID']),
    questionInputLabel() {
      return this.$t('aiChat.questionInputLabel')
    },
    generatingAnswer() {
      return this.$t('aiChat.generatingAnswer')
    },
  },
  watch: {
    processMessages(newVal) {
      if (newVal === messageStatus.Done && this.currentChat) {
        this.saveToDB(this.currentChat)
        this.updateChatHistory(this.currentChat)
        this.processMessages = messageStatus.NotRunning
      }
    },
    messages() {
      this.$nextTick(() => {
        this.$refs.chatMessageStreamEnd?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      })
    },
  },
  methods: {
    ...mapActions('Chat', [
      'historyGenerate',
      'updateCurrentChat',
      'historyUpdate',
      'updateChatHistory',
    ]),
    saveToDB({ messages, id }) {
      const payload = {
        conversation_id: id,
        messages: messages,
        config: {
          vSessionId: this.vSessionId,
        },
      }
      this.historyUpdate(payload)
    },
    loadMoreHistory() {
      this.$emit('loadMoreHistory')
    },
    hideHistory() {
      this.$emit('hideHistory')
    },
    selectHistory(entry) {
      this.messages = [...entry.messages]
    },
    async makeApiRequestWithCosmosDB({ question, conversationId }) {
      this.isLoading = true
      this.showLoadingMessage = true
      this.assistantContent = ''
      this.abortFunc = new AbortController()
      const userMessage: ChatMessage = {
        id: uuidv4(),
        role: USER,
        content: question,
        date: new Date().toISOString(),
      }

      //api call params set here (generate)
      const currentConversation = this.getConversationById(conversationId)
      const conversationMessages = currentConversation?.messages ?? []

      this.messages = [...conversationMessages, userMessage]

      let result = {} as ChatResponse
      let errorResponseMessage = this.$t('aiChat.errorResponseMessage')

      const payload = {
        conversation_id: conversationId,
        messages: this.messages,
        config: {
          vSessionId: this.vSessionId,
          abortSignal: this.abortFunc.signal,
        },
      }
      try {
        const response = await this.historyGenerate(payload)

        if (!response?.ok) {
          const responseJson = await response.json()
          errorResponseMessage =
            responseJson.error === undefined
              ? errorResponseMessage
              : this.parseErrorMessage(responseJson.error)
          let errorChatMsg: ChatMessage = {
            id: uuidv4(),
            role: ERROR,
            content: `${this.$t(
              'aiChat.thereAnErrorResponse'
            )} ${errorResponseMessage}`,
            date: new Date().toISOString(),
          }

          if (currentConversation) {
            this.updateCurrentChat({
              ...currentConversation,
              messages: [...this.messages],
            })
          }
          this.messages = [...this.messages, errorChatMsg]
          return
        }

        if (response?.body) {
          const reader = response.body.getReader()

          let runningText = ''
          let done = false
          while (!done) {
            this.processMessages = messageStatus.Processing
            const readerResult = await reader.read()
            done = readerResult.done
            const value = readerResult.value
            if (done) break

            var text = new TextDecoder('utf-8').decode(value)
            const objects = text.split('\n')
            objects.forEach((obj) => {
              try {
                if (obj !== '' && obj !== '{}') {
                  runningText += obj
                  result = JSON.parse(runningText)
                  if (!result.choices?.[0]?.messages?.[0].content) {
                    errorResponseMessage = NO_CONTENT_ERROR
                    throw Error()
                  }
                  if (result.choices?.length > 0) {
                    result.choices[0].messages.forEach((msg) => {
                      msg.id = result.id
                      msg.date = new Date().toISOString()
                    })
                    if (
                      result.choices[0].messages?.some(
                        (m) => m.role === ASSISTANT
                      )
                    ) {
                      this.showLoadingMessage = false
                    }
                    result.choices[0].messages.forEach((resultObj) => {
                      this.processResultMessage(resultObj)
                    })
                  }
                  runningText = ''
                } else if (result.error) {
                  throw Error(result.error)
                }
              } catch (e) {
                if (!(e instanceof SyntaxError)) {
                  console.error(e)
                  throw e
                } else {
                  console.log('Incomplete message. Continuing...')
                }
              }
            })
          }

          if (conversationId && !currentConversation) {
            console.error('Conversation not found.')
            return
          } else {
            let resultConversation = currentConversation ?? {
              id: result.history_metadata.conversation_id,
              title: result.history_metadata.title,
              messages: [],
              date: result.history_metadata.date,
            }

            this.updateCurrentChat({
              ...resultConversation,
              messages: isEmpty(this.toolMessage)
                ? [
                    ...resultConversation.messages,
                    userMessage,
                    this.assistantMessage,
                  ]
                : [
                    ...resultConversation.messages,
                    userMessage,
                    this.toolMessage,
                    this.assistantMessage,
                  ],
            })
          }
        }
      } catch (error) {
        if (!this.abortFunc.signal.aborted) {
          let errorMessage = `${this.$t(
            'aiChat.errorOccurred'
          )} ${errorResponseMessage}`
          if (result.error?.message) {
            errorMessage = result.error.message
          } else if (typeof result.error === 'string') {
            errorMessage = result.error
          }

          errorMessage = this.parseErrorMessage(errorMessage)

          let errorChatMsg: ChatMessage = {
            id: uuidv4(),
            role: ERROR,
            content: errorMessage,
            date: new Date().toISOString(),
          }
          this.messages = [...this.messages, errorChatMsg]
        } else {
          let errorChatMsg: ChatMessage = {
            id: uuidv4(),
            role: ERROR,
            content: this.$t('aiChat.answersCanNotSaved'),
            date: new Date().toISOString(),
          }
          this.messages = [...this.messages, errorChatMsg]
        }
        if (currentConversation) {
          this.updateCurrentChat({
            ...currentConversation,
            messages: [...currentConversation.messages, userMessage],
          })
        } else {
          this.$emit('loadMoreHistory', 0)
        }
      } finally {
        this.isLoading = false
        this.showLoadingMessage = false
        this.abortFunc = null
        this.processMessages = messageStatus.Done
      }
    },

    getConversationById(conversationId: string) {
      let conversation: Conversation | undefined
      if (conversationId) {
        conversation = this.chatHistory.find(
          (conv: { id: string; messages: ChatMessage[] }) =>
            conv.id === conversationId
        )
      }
      return conversation
    },

    parseCitationFromMessage(message: ChatMessage) {
      if (message?.role && message?.role === 'tool') {
        try {
          const toolMessage = JSON.parse(message.content) as ToolMessageContent
          return toolMessage.citations
        } catch {
          return []
        }
      }
      return []
    },

    stopGenerating() {
      if (this.abortFunc) {
        this.abortFunc.abort()
      }
      this.showLoadingMessage = false
      this.isLoading = false
    },

    tryGetRaiPrettyError(errorMessage: string) {
      try {
        // Using a regex to extract the JSON part that contains "innererror"
        const match = errorMessage.match(/'innererror': ({.*})\}\}/)
        if (match) {
          // Replacing single quotes with double quotes and converting Python-like booleans to JSON booleans
          const fixedJson = match[1]
            .replace(/'/g, '"')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
          const innerErrorJson = JSON.parse(fixedJson)
          let reason = ''
          // Check if jailbreak content filter is the reason of the error
          const jailbreak = innerErrorJson.content_filter_result.jailbreak
          if (jailbreak.filtered === true) {
            reason = 'Jailbreak'
          }

          // Returning the prettified error message
          if (reason !== '') {
            return (
              'The prompt was filtered due to triggering Azure OpenAI’s content filtering system.\n' +
              'Reason: This prompt contains content flagged as ' +
              reason +
              '\n\n' +
              'Please modify your prompt and retry. Learn more: https://go.microsoft.com/fwlink/?linkid=2198766'
            )
          }
        }
      } catch (e) {
        console.error('Failed to parse the error:', e)
      }
      return errorMessage
    },

    parseErrorMessage(errorMessage: string) {
      let errorCodeMessage = errorMessage.substring(
        0,
        errorMessage.indexOf('-') + 1
      )
      const innerErrorCue = "{\\'error\\': {\\'message\\': "
      if (errorMessage.includes(innerErrorCue)) {
        try {
          let innerErrorString = errorMessage.substring(
            errorMessage.indexOf(innerErrorCue)
          )
          if (innerErrorString.endsWith("'}}")) {
            innerErrorString = innerErrorString.substring(
              0,
              innerErrorString.length - 3
            )
          }
          innerErrorString = innerErrorString.replace(/\\'/g, "'")
          let newErrorMessage = errorCodeMessage + ' ' + innerErrorString
          errorMessage = newErrorMessage
        } catch (e) {
          console.error('Error parsing inner error message: ', e)
        }
      }

      return this.tryGetRaiPrettyError(errorMessage)
    },

    processResultMessage(resultMessage: ChatMessage) {
      if (resultMessage.role === ASSISTANT) {
        this.assistantContent += resultMessage.content
        this.assistantMessage = resultMessage
        this.assistantMessage.content = this.assistantContent
        const messageId = this.messages.findIndex(
          (msg) => msg.id === resultMessage.id && msg.role === ASSISTANT
        )
        if (messageId !== -1) {
          this.messages[messageId].content = this.assistantContent
        } else {
          this.messages = [...this.messages, resultMessage]
        }
      }

      if (resultMessage.role === TOOL) {
        this.toolMessage = resultMessage
        this.messages = [...this.messages, this.toolMessage]
      }
    },
  },
})
</script>

<style lang="scss">
.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: radial-gradient(
    108.78% 108.78% at 50.02% 19.78%,
    #ffffff 57.29%,
    #eef6fe 100%
  );
  border-radius: 0px;
  .chat-root {
    width: 100%;
    flex-flow: row nowrap;
  }
  .chat-content {
    width: 60%;
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    max-height: calc(80vh - 40px);
    background: radial-gradient(
      108.78% 108.78% at 50.02% 19.78%,
      #fff 57.29%,
      #eef6fe
    );
  }
}

.chat-empty-state {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.chat-empty-state-title {
  margin-top: 20px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  align-items: flex-end;
  text-align: center;
  letter-spacing: -0.01em;
  color: #616161;
}

.chat-message-stream {
  flex-grow: 1;
  max-width: calc(38vw - 24px);
  width: 100%;
  overflow: auto;
  padding-left: 24px;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
}

.chat-message-user {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.chat-message-user-message {
  display: flex;
  padding: 20px;
  background: #edf5fd;
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12);
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #242424;
  order: 0;
  flex-grow: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 80%;
}

.chat-message-gpt {
  margin-bottom: 12px;
  max-width: 90%;
  display: flex;
}

.chat-message-error {
  padding: 20px;
  border-radius: 8px;
  box-shadow: rgba(182, 52, 67, 1) 1px 1px 2px, rgba(182, 52, 67, 1) 0px 0px 1px;
  color: #242424;
  flex: none;
  order: 0;
  flex-grow: 0;
  max-width: 800px;
  margin-bottom: 12px;
}

.chat-message-error-content {
  font-family: 'segoe ui';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  word-wrap: break-word;
  gap: 12px;
  align-items: center;
}

.chat-input {
  position: sticky;
  flex: 0 0 60px;
  padding: 0px;
  width: calc(100% - 40px);
  margin-bottom: 80px;
  margin-top: 8px;
  margin-left: 20px;
}

.clear-chat-broom {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 40px;
  height: 40px;
  left: 7px;
  top: 13px;
  color: #ffffff;
  border-radius: 4px;
  z-index: 1;
}

.stop-generating-container {
  position: relative;
  height: 32px;
  bottom: 32px;
}

.stop-generating-icon {
  width: 14px;
  height: 14px;
  color: #424242;
}

.stop-generating-text {
  width: 103px;
  height: 20px;
  font-style: normal;
  font-weight: 600 !important;
  font-size: 10px !important;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #242424;
  flex: none;
  order: 0;
  flex-grow: 0;
}

a {
  padding-left: 5px;
  padding-right: 5px;
}

@media (max-width: 480px) {
  .chat-input {
    width: 90%;
    max-width: 90%;
  }
}
</style>
